import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { getTourDefinition, tourIdForPathname } from "@/lib/tours/registry";
import { startShellTour, startTour } from "@/lib/tours/startTour";
import { hasSeenTour, resetAllTours, resetTourSeen } from "@/lib/tours/tourStorage";
import type { TourId } from "@/lib/tours/types";

interface TourContextValue {
  currentTourId: TourId | null;
  startCurrentPageTour: (options?: { force?: boolean }) => void;
  startTourById: (tourId: TourId, options?: { force?: boolean }) => void;
  resetTour: (tourId: TourId) => void;
  resetAllToursSeen: () => void;
  hasSeenTour: (tourId: TourId) => boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const currentTourId = tourIdForPathname(pathname);

  const startCurrentPageTour = useCallback(
    (options?: { force?: boolean }) => {
      if (!currentTourId) return;
      startTour(currentTourId, {
        force: options?.force,
        includeShell: currentTourId === "dashboard" && !hasSeenTour("shell"),
      });
    },
    [currentTourId],
  );

  const startTourById = useCallback((tourId: TourId, options?: { force?: boolean }) => {
    if (tourId === "shell") {
      startShellTour();
      return;
    }
    startTour(tourId, { force: options?.force, includeShell: false });
  }, []);

  const resetTour = useCallback((tourId: TourId) => {
    resetTourSeen(tourId);
  }, []);

  const resetAllToursSeen = useCallback(() => {
    resetAllTours();
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({
      currentTourId,
      startCurrentPageTour,
      startTourById,
      resetTour,
      resetAllToursSeen,
      hasSeenTour,
    }),
    [currentTourId, resetAllToursSeen, resetTour, startCurrentPageTour, startTourById],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used within TourProvider");
  return ctx;
}

export function useCurrentTourTitle(): string | null {
  const { currentTourId } = useTour();
  if (!currentTourId) return null;
  return getTourDefinition(currentTourId).title;
}
